import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PaymentMethodManager = ({ currentMethod, onUpdate }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);

  // Mock payment methods
  const paymentMethods = [
    {
      id: 'pm_1',
      type: 'card',
      brand: 'visa',
      last4: '4242',
      expiryMonth: 12,
      expiryYear: 2027,
      isDefault: true
    }
  ];

  const availablePaymentOptions = [
    {
      id: 'stripe',
      name: 'Credit/Debit Card',
      icon: 'CreditCard',
      description: 'Visa, Mastercard, American Express',
      status: 'automatic'
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: 'Wallet',
      description: 'Pay securely with your PayPal account',
      status: 'automatic'
    },
    {
      id: 'zelle',
      name: 'Zelle',
      icon: 'Smartphone',
      description: 'Bank transfer (manual approval required)',
      status: 'manual'
    }
  ];

  const handleAddPaymentMethod = async () => {
    setLoading(true);
    try {
      // In production, integrate with Stripe/PayPal API
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowAddModal(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error adding payment method:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePaymentMethod = async (methodId) => {
    if (!confirm('Are you sure you want to remove this payment method?')) return;
    
    setLoading(true);
    try {
      // In production, call API to remove payment method
      await new Promise(resolve => setTimeout(resolve, 1000));
      onUpdate?.();
    } catch (error) {
      console.error('Error removing payment method:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    setLoading(true);
    try {
      // In production, call API to set default payment method
      await new Promise(resolve => setTimeout(resolve, 1000));
      onUpdate?.();
    } catch (error) {
      console.error('Error setting default payment method:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Payment Methods */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Saved Payment Methods</h3>
            <Button
              iconName="Plus"
              onClick={() => setShowAddModal(true)}
            >
              Add Payment Method
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {paymentMethods?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="CreditCard" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">No payment methods saved</p>
              <Button onClick={() => setShowAddModal(true)}>Add Your First Payment Method</Button>
            </div>
          ) : (
            paymentMethods?.map((method) => (
              <div
                key={method?.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Icon name="CreditCard" className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900 capitalize">
                        {method?.brand} •••• {method?.last4}
                      </span>
                      {method?.isDefault && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Expires {method?.expiryMonth?.toString()?.padStart(2, '0')}/{method?.expiryYear}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!method?.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetDefault(method?.id)}
                      disabled={loading}
                    >
                      Set as Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => handleRemovePaymentMethod(method?.id)}
                    disabled={loading || method?.isDefault}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Payment Security Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Icon name="Shield" className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Secure Payment Processing</h3>
            <p className="text-sm text-gray-600 mb-3">
              All payment information is encrypted and securely processed through our payment partners. 
              We never store your complete card details on our servers.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Icon name="Lock" className="w-4 h-4 text-gray-600" />
                <span className="text-xs text-gray-600">256-bit SSL Encryption</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Shield" className="w-4 h-4 text-gray-600" />
                <span className="text-xs text-gray-600">PCI DSS Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Payment Method Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Add Payment Method</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {availablePaymentOptions?.map((option) => (
                <button
                  key={option?.id}
                  onClick={() => setSelectedMethod(option?.id)}
                  className={`w-full flex items-center gap-4 p-4 border-2 rounded-lg transition-all ${
                    selectedMethod === option?.id
                      ? 'border-primary bg-primary/5' :'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Icon name={option?.icon} className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-sm font-semibold text-gray-900">{option?.name}</p>
                    <p className="text-xs text-gray-600">{option?.description}</p>
                  </div>
                  {selectedMethod === option?.id && (
                    <Icon name="CheckCircle" className="w-5 h-5 text-primary" />
                  )}
                </button>
              ))}
            </div>

            {selectedMethod === 'stripe' && (
              <div className="space-y-4 mb-6">
                <Input
                  label="Card Number"
                  placeholder="1234 5678 9012 3456"
                  type="text"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Expiry Date"
                    placeholder="MM/YY"
                    type="text"
                  />
                  <Input
                    label="CVC"
                    placeholder="123"
                    type="text"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                fullWidth
                variant="outline"
                onClick={() => setShowAddModal(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                onClick={handleAddPaymentMethod}
                loading={loading}
              >
                Add Payment Method
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodManager;